import { init as initApm, AgentConfigOptions, ApmBase } from '@elastic/apm-rum'
import { AxiosError } from 'axios'
import { ELASTIC_APM_ENVIRONMENT_LABEL, CLIENT_ADDRESS, APPLICATION_VERSION } from '../environment.ts'

class SingletonLogger {
  static instance: ApmBase
  static config = {
    serviceName: 'kmr-srp-tool',
    serverUrl: 'https://f118e1cc2e8d454a9cb1165e76783601.apm.ap-southeast-2.aws.cloud.es.io:443',
    serviceVersion: APPLICATION_VERSION,
    active: CLIENT_ADDRESS?.includes('localhost') ? false : true,
    instrument: true,
    disableInstrumentations: ['page-load', 'history', 'eventtarget', 'click', 'xmlhttprequest'],
    environment: ELASTIC_APM_ENVIRONMENT_LABEL ?? 'local',
    logLevel: 'error',
  }

  /**
   * The Singleton's constructor should always be private to prevent direct
   * construction calls with the `new` operator.
   */
  // eslint-disable-next-line  @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance() {
    if (!this.instance) {
      this.instance = initApm(this.config as AgentConfigOptions)
    }
    return this.instance
  }
}

export const elasticApm = SingletonLogger.getInstance()

// captureHttpError enriches AxiosErrors before sending to APM. The raw
// captureError(axiosError) call only surfaces "Request failed with status code N"
// with no request detail. Here we set custom context (attaches when a transaction
// is active) and rewrite the error message so the URL, method, and status are
// always visible in the APM error list even without transaction context.
export function captureHttpError(error: AxiosError, additionalContext?: Record<string, unknown>): void {
  const method = (error.config?.method ?? 'unknown').toUpperCase()
  const url = error.config?.url ?? 'unknown'
  const status = error.response?.status ?? 'unknown'

  elasticApm.setCustomContext({
    http: { method, url, status, responseData: error.response?.data },
    ...additionalContext,
  })

  // Rewrite the message so it's useful even when there's no active transaction
  // (XHR instrumentation is disabled, so setCustomContext is often a no-op).
  const enriched = new Error(`${method} ${url} [${status}]`)
  enriched.name = 'HttpError'
  enriched.stack = error.stack
  elasticApm.captureError(enriched)
}
